import type { DisciplineSlug } from "@jumpbean/shared";
import type { DisciplineReportStats } from "../../hooks/useProgressReport";

export interface DisciplineBreakdownProps {
  disciplines: DisciplineReportStats[];
  totalStars: number;
}

// Discipline configuration with colors and emojis
const disciplineConfig: Record<
  DisciplineSlug,
  { emoji: string; bgClass: string; barClass: string; textClass: string }
> = {
  math: {
    emoji: "🦄",
    bgClass: "bg-gradient-to-r from-magic-100 to-luna-light/30",
    barClass: "from-magic-400 to-luna",
    textClass: "text-magic-600",
  },
  spelling: {
    emoji: "🧚",
    bgClass: "bg-gradient-to-r from-meadow-100 to-petal-light/30",
    barClass: "from-meadow-400 to-petal",
    textClass: "text-meadow-600",
  },
  patterns: {
    emoji: "🐉",
    bgClass: "bg-gradient-to-r from-fairy-100 to-sunshine-100",
    barClass: "from-fairy-400 via-sunshine-400 to-sky-400",
    textClass: "text-fairy-600",
  },
  typing: {
    emoji: "🐱",
    bgClass: "bg-gradient-to-r from-sky-100 to-whiskers-light/30",
    barClass: "from-sky-400 to-whiskers",
    textClass: "text-sky-600",
  },
  programming: {
    emoji: "🦉",
    bgClass: "bg-gradient-to-r from-pixel-light/30 to-sunshine-100",
    barClass: "from-pixel to-sunshine-500",
    textClass: "text-pixel",
  },
};

interface DisciplineRowProps {
  discipline: DisciplineReportStats;
  maxStars: number;
}

function DisciplineRow({ discipline, maxStars }: DisciplineRowProps) {
  const config = disciplineConfig[discipline.slug as DisciplineSlug] || disciplineConfig.math;
  const percentage = maxStars > 0 ? (discipline.starsEarned / maxStars) * 100 : 0;

  return (
    <div className={`rounded-xl p-4 ${config.bgClass} border border-neutral-100/50`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl" role="img" aria-hidden="true">
            {config.emoji}
          </span>
          <span className="font-display font-semibold text-body-lg text-neutral-800">
            {discipline.name}
          </span>
        </div>
        <div className="flex items-center gap-3 font-body text-sm">
          <span className={`font-medium ${config.textClass}`}>
            {discipline.starsEarned} ⭐
          </span>
          <span className="text-neutral-500">
            {discipline.challengesCompleted} done
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-3 bg-neutral-200/50 rounded-full overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${config.barClass} rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Percentage label */}
      <div className="flex justify-between items-center mt-2">
        <span className="font-body text-xs text-neutral-500">
          {discipline.percentOfTotal}% of activity
        </span>
        <span className={`font-body text-xs font-medium ${config.textClass}`}>
          {Math.round(percentage)}%
        </span>
      </div>
    </div>
  );
}

/**
 * Discipline breakdown section showing progress in each learning area.
 * Features colorful progress bars matching each discipline's theme.
 */
export function DisciplineBreakdown({ disciplines, totalStars }: DisciplineBreakdownProps) {
  // Sort by stars earned (highest first)
  const sortedDisciplines = [...disciplines].sort(
    (a, b) => b.starsEarned - a.starsEarned
  );

  // Find max stars to normalize the bars
  const maxStars = Math.max(...disciplines.map((d) => d.starsEarned), 1);

  // Check if there's any activity
  const hasActivity = totalStars > 0;

  return (
    <div className="space-y-4">
      <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
        <span className="text-xl">📚</span>
        By Discipline
      </h2>

      {hasActivity ? (
        <div className="space-y-3">
          {sortedDisciplines.map((discipline) => (
            <DisciplineRow
              key={discipline.slug}
              discipline={discipline}
              maxStars={maxStars}
            />
          ))}
        </div>
      ) : (
        <div className="bg-neutral-50 rounded-xl p-6 text-center border border-neutral-100">
          <span className="text-4xl mb-2 block">📝</span>
          <p className="font-body text-neutral-600">
            No challenges completed this period.
          </p>
          <p className="font-body text-sm text-neutral-500 mt-1">
            Encourage your child to explore a discipline!
          </p>
        </div>
      )}
    </div>
  );
}

export default DisciplineBreakdown;
